/**
 * The dashboard, in one call.
 *
 * Network-only. Every figure here is computed from server-side rollups, and a
 * cached copy on a terminal would be a number that looks authoritative and is
 * hours old — which is worse than a spinner, because nobody questions it.
 */

import { api } from "@/api/client";

/** Cost and profit are absent for anybody without `reports.view_margin`. */
export interface PeriodSummary {
  from: string;
  to: string;
  gross_sales: string;
  discount_total: string;
  tax_total: string;
  net_sales: string;
  transaction_count: number;
  refund_count: number;
  item_count: string;
  average_basket: string;
  cost_total?: string;
  gross_profit?: string;
  margin_percent?: string;
}

export interface TrendPoint {
  bucket: string;
  gross_sales: string;
  transaction_count: number;
  cost_total?: string;
  gross_profit?: string;
}

export interface HourPoint {
  hour: number;
  gross_sales: string;
  transaction_count: number;
  average_per_day: string;
}

export interface ProductRow {
  product_id: string;
  description: string;
  gross_sales: string;
  quantity: string;
  basket_count: number;
  cost_total?: string;
  gross_profit?: string;
}

export interface PaymentRow {
  method: string;
  amount: string;
  transaction_count: number;
}

export interface Overview {
  as_at: string;
  today: PeriodSummary;
  week: PeriodSummary;
  month: PeriodSummary;
  month_before: PeriodSummary;
  trend: TrendPoint[];
  by_hour: HourPoint[];
  top_products: ProductRow[];
  payment_mix: PaymentRow[];
}

export interface Insight {
  code: string;
  tone: "good" | "bad" | "neutral";
  title: string;
  detail: string;
  evidence: Record<string, string>;
}

export const analyticsRepository = {
  overview: (days = 30) => api.get<Overview>(`/analytics/overview/?days=${days}`),
  insights: () => api.get<Insight[]>("/analytics/insights/"),
};
